import { Loyalty } from '@getjust/api-schema';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { loyaltyBalanceAtom } from '$business/atoms/loyalty';
import { useCart } from '$src/hooks/queries';
import { useFormMessageAtom } from '$src/hooks/state';
import { useROEmailAtom } from '$src/hooks/state/useEmailAtom';
import { gateway } from '$src/http';

const LOYALTY_BALANCE_QUERY_KEY = 'QUERY/LOYALTY_BALANCE';

export const useLoyaltyBalance = () => {
  const { formMessage } = useFormMessageAtom();
  const { data: cart } = useCart();
  const email = useROEmailAtom();
  const phone = formMessage?.currentUser?.phone;
  const platformCustomerId = formMessage?.currentUser?.id;
  const currency = cart?.currency;
  const [, setLoyaltyBalance] = useAtom(loyaltyBalanceAtom);

  const shopId = formMessage?.shopId;
  const enabled = Boolean(shopId && (email || phone));

  const query = useQuery({
    queryKey: [LOYALTY_BALANCE_QUERY_KEY, shopId, email, phone, platformCustomerId, currency],

    queryFn: async () => {
      if (!shopId) {
        return { outcome: 'errored' as const, error: new Error('Shop ID is required') };
      }

      const response = await gateway.get<Loyalty.LoyaltyGetBalanceResponseBody>(
        `/api/${encodeURIComponent(shopId)}/loyalty/balance`,
        { params: { email, phone, platformCustomerId, currency } },
      );
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    setLoyaltyBalance(query.data?.outcome === 'found' ? query.data : undefined);
  }, [query.data, setLoyaltyBalance]);

  return {
    loyaltyBalance: query.data?.outcome === 'found' ? query.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    isConfigured: query.data ? query.data.outcome !== 'notConfigured' : false,
  };
};
